import {
  AssistantPageContext,
  pageContextKey,
} from '@components/Assistant/assistantPageContext'
import { PRIVACY_NOTICE_REQUIRED } from '@components/Assistant/errorMessages'
import { ApiService } from '@services/ApiService'
import {
  AssistantCitation,
  AssistantClarificationEvent,
  AssistantPart,
} from '@services/modules/AssistantStream'
import { makeAutoObservable, runInAction } from 'mobx'

import { AssistantSession } from './assistantSession'

export {
  AssistantSession,
  FRAME_FALLBACK_MS,
  RECONCILE_DELAY_MS,
  RECONCILE_RETRY_DELAYS_MS,
} from './assistantSession'
export type { AssistantSessionContextSource } from './assistantSession'

/** The assistant's own error codes (ROLE_NOT_ALLOWED, FEATURE_DISABLED…), not framework ones. */
const ASSISTANT_CODE_PATTERN = /^[A-Z][A-Z_]*$/

/** The longest voice recording when the capabilities do not say (server default). */
const DEFAULT_VOICE_MAX_SECONDS = 120

/**
 * How old the capabilities may be when the drawer is opened before they are read again: the host
 * can switch the assistant off (or a skill, or the role's audience) while the page stays open.
 */
export const CAPABILITIES_MAX_AGE_MS = 60000

/**
 * How many placement sessions (one per placement and record, see sessionFor) are kept in memory.
 * The least recently used one goes first; one whose turn or action is still running never does.
 */
export const MAX_PLACEMENT_SESSIONS = 5

export interface AssistantConversationSummary {
  id: string
  title: string | null
  lastMessageAt: string
  isArchived: boolean
  status: string
}

/** The notice a case manager or specialist reads once before their first question. */
export interface AssistantPrivacyNotice {
  /** Sent back when the notice is closed, so a newer text is shown again. */
  version: string
  title: string
  /** One paragraph each. */
  lines: string[]
}

/** Compares two server timestamps (their fraction digits vary, so not as text). */
const isOlder = (a: string, b: string) => Date.parse(a) < Date.parse(b)

export interface AssistantCommand {
  name: string
  description: string
  usageHint: string
  toolName: string
}

export interface AssistantMentionType {
  type: string
  aliases: string[]
  displayName: string
  /** The typeahead of this kind returns nothing for a shorter text (absent from an older server). */
  minQueryLength?: number
  /** How to search this kind, shown while too little is typed. */
  searchHint?: string | null
}

export interface AssistantMentionResult {
  entityType: string
  id: string
  label: string
  description?: string
}

/** A tagged record living in the composer draft ("@Dr Frank Gibson"). */
export interface AssistantDraftMention {
  display: string
  entityType: string
  id: string
  label: string
}

export interface AssistantSuggestedQuery {
  category: string
  text: string
  prefillOnly?: boolean
}

export interface AssistantBriefingItem {
  label: string
  detail?: string
  overdue?: boolean
  route?: string
  query?: Record<string, string>
  /** Dashboard tab title for a record link (the case number). */
  tabTitle?: string
}

export interface AssistantBriefingSection {
  key: string
  title: string
  count: number
  items: AssistantBriefingItem[]
  askPrompt?: string
}

export interface AssistantBriefing {
  date: string
  generatedAt: string
  sections: AssistantBriefingSection[]
}

export interface AssistantDocSummary {
  key: string
  title: string
  summary: string
  commands: string[]
}

export interface AssistantDocPage extends AssistantDocSummary {
  markdown: string
}

export interface AssistantChatMessage {
  id?: string
  role: 'User' | 'Assistant'
  content: string
  citations?: AssistantCitation[]
  clarification?: AssistantClarificationEvent
  clarificationAnswered?: boolean
  /** Option value the user picked (renders the selected state on the option card). */
  clarificationSelectedValue?: string
  feedbackRating?: number | null
  partial?: boolean
  /** A question the content filter blocked: shown, but never sent to the assistant again. */
  excludedFromContext?: boolean
  /** Structured parts rendered under the answer (e.g. "Open in list view"). */
  parts?: AssistantPart[]
}

/** 1 = good answer, -1 = bad answer, 0 = the rating is taken back. */
export type AssistantFeedbackRating = 1 | -1 | 0

/** Why an answer was rated down: the codes the server accepts, with the wording shown for each. */
export const ASSISTANT_FEEDBACK_REASONS = [
  { code: 'WRONG_DATA', label: 'Wrong data' },
  { code: 'DID_NOT_ANSWER', label: 'Did not answer' },
  { code: 'TOO_SLOW', label: 'Too slow' },
  { code: 'OTHER', label: 'Other' },
] as const

export type AssistantFeedbackReason =
  (typeof ASSISTANT_FEEDBACK_REASONS)[number]['code']

/** The longest note a rating can carry; the server refuses a longer one. */
export const ASSISTANT_FEEDBACK_NOTE_MAX = 1000

export interface AssistantFeedbackDetails {
  reason?: AssistantFeedbackReason
  comment?: string
}

/** The state a rating request keeps per answer (see AssistantSession.submitFeedback). */
export interface AssistantFeedbackRequestState {
  stored: number | null | undefined
  pending: number
  last: Promise<boolean> | null
}

/**
 * A mounted entry point of an embedded placement that takes Ctrl+K while it is mounted (an entry
 * with shortcut 'pane', e.g. the Case history modal's "Ask K-Assist"): see registerPaneEntry.
 */
export interface AssistantPaneEntry {
  /** The screen's own id (embeddedHost while its pane is shown). */
  host: string
  /** Opens its pane the way its button does. */
  open: () => void
}

/**
 * Global singleton store for the AI Assistant (same pattern as chatbotStore). It holds what the
 * user has app-wide: what the assistant offers them (capabilities), the privacy notice, the
 * conversation list, the guide pages, the briefing, and which view shows the Assistant (the
 * drawer or a screen's pane). Each view's chat is an AssistantSession: `drawer` for the drawer
 * and the standalone page, and one per placement and record (sessionFor). The session API read
 * on the store itself (messages, sendMessage…) is the drawer's, for callers outside a panel.
 */
export class AssistantStore {
  isOpen = false
  /**
   * The screen that shows the Assistant in a pane of its own (the Case history's "Ask
   * K-Assist"), or null while none does. One host at a time, and never together with the drawer:
   * only one composer is on screen.
   */
  embeddedHost: string | null = null
  docs: AssistantDocSummary[] = []
  docPages: Record<string, AssistantDocPage> = {}
  /** The pages of the history loaded so far, newest first. */
  conversations: AssistantConversationSummary[] = []
  /** How many conversations match the history's filter on the server, loaded or not. */
  conversationsTotal = 0
  /** Older conversations than the loaded ones exist (see loadMoreConversations). */
  conversationsHasMore = false
  /** The text the history is filtered by (titles that contain it); empty = no filter. */
  conversationFilter = ''
  /**
   * The filter the conversations on screen were read with. While it is not the filter above, the
   * list still shows the rows of the previous one and the answer for the new one is on its way
   * (see conversationsFiltering).
   */
  private loadedConversationFilter = ''
  loadingMoreConversations = false
  /**
   * Set for a case manager or specialist who has not closed the privacy notice yet: it is shown
   * in place of the chat until they do. The server remembers it per user.
   */
  privacyNotice: AssistantPrivacyNotice | null = null
  commands: AssistantCommand[] = []
  suggestions: AssistantSuggestedQuery[] = []
  mentionTypes: AssistantMentionType[] = []
  /** Why the assistant cannot be used by this user (server code); null while it can. */
  unavailableReason: string | null = null
  /**
   * The composer offers voice input (a question spoken, transcribed by the server): the
   * assistant is available to this user, the tenant has voice input on and speech is set up.
   * Off until the capabilities say otherwise.
   */
  voiceInput = false
  /** The longest recording voice input takes; it stops by itself at this length. */
  voiceMaxSeconds = DEFAULT_VOICE_MAX_SECONDS

  /**
   * The drawer's chat, also the standalone page's. Its record is the one on screen (the
   * dashboard tab, or the standalone page's snapshot).
   */
  readonly drawer: AssistantSession
  /** The placements' chats, per placement and record, least recently used first. */
  private readonly sessions = new Map<string, AssistantSession>()
  /**
   * The entries that take Ctrl+K for their pane, in mount order (the last mounted on top); see
   * registerPaneEntry. Read when the key is pressed only, so not observable.
   */
  private readonly paneEntries: AssistantPaneEntry[] = []
  /**
   * The conversation list has been asked for at least once on this page (the drawer's History,
   * opening the drawer). Until then a placement's turn does not read it (see
   * refreshConversationsAfterTurn): a pane has no list on screen.
   */
  private conversationListRequested = false

  /** Identifies the newest request for the history; an older one that answers late is ignored. */
  private conversationListRequest = 0
  private capabilitiesLoaded = false
  /** When the capabilities were last answered by the server (Date.now()). */
  private capabilitiesLoadedAt = 0
  /** Identifies the newest capabilities request; an older one that answers late is ignored. */
  private capabilitiesRequest = 0
  private docsLoaded = false
  /**
   * Titles learned from the stream (the `title` event), each with the order it arrived in: a
   * conversation list requested before a title arrived still carries the old one.
   */
  private streamedTitles = new Map<string, { title: string; seq: number }>()
  /**
   * The rating requests under way, per answer: what the server holds (`stored`), how many
   * requests have not been answered yet, and the newest one. Not observable. Shared by the
   * sessions: an answer is shown by one session at a time.
   */
  readonly feedbackRequests = new Map<string, AssistantFeedbackRequestState>()
  private titleSeq = 0

  constructor() {
    this.drawer = new AssistantSession(this, 'drawer')
    makeAutoObservable<
      AssistantStore,
      'sessions' | 'paneEntries' | 'conversationListRequested'
    >(this, {
      drawer: false,
      sessions: false,
      paneEntries: false,
      conversationListRequested: false,
      // Read inside actions only; the session map itself is not observable.
      allSessions: false,
      feedbackRequests: false,
      // Called while a panel renders: it creates or finds a session, it changes nothing shown.
      sessionFor: false,
    })
  }

  // ---- Sessions ---------------------------------------------------------------------------

  /** Every chat that exists now: the drawer's and the placements'. */
  get allSessions(): AssistantSession[] {
    return [this.drawer, ...this.sessions.values()]
  }

  /**
   * The chat of `placementId` about `context` (key `<placement>:<record>`, e.g.
   * `case-history:case:<id>`), created on first use and kept while the page is open, so the
   * placement resumes it the next time it is shown. The modal and the full page of the same
   * record share it (only one of them is on screen at a time).
   */
  sessionFor = (
    placementId: string,
    context: AssistantPageContext | null
  ): AssistantSession => {
    const key = `${placementId}:${pageContextKey(context) ?? 'none'}`
    let session = this.sessions.get(key)
    if (session) {
      // Most recently used goes last.
      this.sessions.delete(key)
    } else {
      session = new AssistantSession(this, key, { kind: 'host', context })
    }
    this.sessions.set(key, session)
    this.evictSessions(session)
    return session
  }

  /**
   * Keeps at most MAX_PLACEMENT_SESSIONS sessions: the least recently used go first, but never
   * `keep` nor one whose turn or confirm/cancel is still running.
   */
  private evictSessions = (keep: AssistantSession) => {
    if (this.sessions.size <= MAX_PLACEMENT_SESSIONS) {
      return
    }
    for (const [key, session] of this.sessions) {
      if (this.sessions.size <= MAX_PLACEMENT_SESSIONS) {
        break
      }
      if (
        session === keep ||
        session.isTurnRunning ||
        session.actionInFlight !== null
      ) {
        continue
      }
      this.sessions.delete(key)
      runInAction(() => session.dispose())
    }
  }

  /**
   * Drops every placement session (their streams, timers and frames go with them) and stops the
   * drawer's: the user's session ended (sign-out, another tenant). Nothing is kept in storage.
   */
  disposeSessions = () => {
    for (const session of this.sessions.values()) {
      session.dispose()
    }
    this.sessions.clear()
    this.drawer.stop()
  }

  /**
   * The session other than `except` that holds conversation `id` and is still answering in it
   * (a turn, or a confirm/cancel): it cannot be opened anywhere else until that is over, or two
   * views would show the same conversation with different content.
   */
  isConversationBusyElsewhere = (id: string, except: AssistantSession) =>
    this.allSessions.some(
      session =>
        session !== except &&
        session.activeConversationId === id &&
        (session.isTurnRunning || session.actionInFlight !== null)
    )

  /**
   * One conversation, one live session: `by` has opened conversation `id`, so any other session
   * that held it lets it go and is back to an empty chat (its draft stays).
   */
  conversationOpened = (by: AssistantSession, id: string) => {
    for (const session of this.allSessions) {
      if (session !== by && session.activeConversationId === id) {
        session.letGoOfConversation()
      }
    }
  }

  /**
   * "Open in full K-Assist" can move `session`'s conversation to the drawer now: neither it nor
   * the drawer is answering (the drawer could not switch conversation meanwhile).
   */
  canHandOffToDrawer = (session: AssistantSession) =>
    session !== this.drawer &&
    !session.isBusy &&
    session.actionInFlight === null &&
    !this.drawer.isTurnRunning

  /**
   * "Open in full K-Assist": the placement's conversation, with what is typed in its composer,
   * moves to the drawer (one way: the placement starts from an empty chat the next time it is
   * shown), the pane closes and the drawer opens. `onHandOff` runs before the drawer opens: a
   * host that cannot be on screen with the drawer (the Case history modal) closes itself there.
   * Not while either of them is answering (see canHandOffToDrawer).
   */
  handOffToDrawer = (session: AssistantSession, onHandOff?: () => void) => {
    if (!this.canHandOffToDrawer(session)) {
      return
    }
    const drawer = this.drawer
    const conversationId = session.activeConversationId
    // Mentions are off in a placement's composer: the text is all there is to carry. Not an
    // answer to a clarification that was handed back to it while the server may already hold
    // it: sent from the drawer, it would be asked a second time as a new question.
    const draft = session.draftWithoutHandedBack()
    // What the drawer had typed itself, kept when the pane carries nothing.
    const drawerText = drawer.draftText
    const drawerMentions = [...drawer.draftMentions]
    // Moved, not copied: the placement lets go first, so nothing holds the conversation twice.
    session.newConversation()
    // The drawer leaves its own conversation first: when the pane's cannot be loaded, it is on
    // an empty chat (with the load error), not on its previous one, where the next question
    // would go.
    drawer.newConversation()
    if (conversationId) {
      drawer.selectConversation(conversationId)
    }
    if (draft.trim()) {
      drawer.setDraft(draft)
    } else if (drawerText.trim()) {
      drawer.draftText = drawerText
      drawer.draftMentions = drawerMentions
      drawer.draftRevision++
    }
    drawer.showHistory = false
    drawer.showGuide = false
    this.closeEmbedded()
    onHandOff?.()
    this.openDrawer()
    drawer.requestComposerFocus()
  }

  /**
   * Ctrl+K opens the pane of `entry` while it is registered (an entry point with shortcut 'pane'
   * that is mounted and available); the newest registration is on top. Returns what takes it
   * back.
   */
  registerPaneEntry = (entry: AssistantPaneEntry) => {
    this.paneEntries.push(entry)
    return () => {
      const index = this.paneEntries.lastIndexOf(entry)
      if (index >= 0) {
        this.paneEntries.splice(index, 1)
      }
    }
  }

  // ---- Visibility ---------------------------------------------------------------------------

  /**
   * Ctrl+K: hides a pane that shows the Assistant; else opens the pane of the entry on top of
   * the registered ones (the Case history modal's); else opens or closes the drawer, with the
   * drawer's own conversation.
   */
  toggle = () => {
    if (this.embeddedHost !== null) {
      this.closeEmbedded()
      return
    }
    const entry = this.paneEntries[this.paneEntries.length - 1]
    if (entry) {
      entry.open()
      return
    }
    this.isOpen ? this.close() : this.openDrawer()
  }

  /** Opens the drawer; a pane that shows the Assistant is closed (the drawer takes it over). */
  openDrawer = () => {
    this.embeddedHost = null
    this.isOpen = true
    this.prepareToShow()
  }

  /**
   * Shows the Assistant in the pane of `host` (a screen's own id): the drawer and the pane of any
   * other screen are closed. `conversationList: false` for a placement without the History
   * button: the list is not read for it.
   */
  openEmbedded = (
    host: string,
    { conversationList = true }: { conversationList?: boolean } = {}
  ) => {
    this.isOpen = false
    this.embeddedHost = host
    this.prepareToShow(conversationList)
  }

  /** Hides the pane of `host`, or of whichever screen shows it when no host is given. */
  closeEmbedded = (host?: string) => {
    if (host === undefined || this.embeddedHost === host) {
      this.embeddedHost = null
    }
  }

  close = () => {
    this.isOpen = false
  }

  /**
   * What the Assistant reads each time it is shown: what it offers this user (shared by every
   * view) and, for a view with the History button (`conversationList`), the history.
   */
  private prepareToShow = (conversationList = true) => {
    if (conversationList && this.conversations.length === 0) {
      this.loadConversations()
    }
    // Asked again when the last answer was "not available" (the switch may be back on) and when
    // it is no longer fresh (the switch may have gone off): the user reads that here, not after
    // typing a question.
    this.loadCapabilities(
      this.unavailableReason !== null ||
        Date.now() - this.capabilitiesLoadedAt > CAPABILITIES_MAX_AGE_MS
    )
  }

  // ---- Capabilities -------------------------------------------------------------------------

  /**
   * Whether the assistant can use a tool for this user. The commands come from
   * the server already limited to the skills that are on for the tenant, so a
   * tool without a command there is off. True until they have loaded.
   */
  canUseTool = (toolName: string) =>
    !this.capabilitiesLoaded || this.commands.some(c => c.toolName === toolName)

  /**
   * The server has answered what the assistant offers this user (or that it is not available to
   * them). Until then canUseTool says yes to every tool: a control that only makes sense with one
   * tool waits for this instead of showing and then going away.
   */
  get capabilitiesKnown() {
    return this.capabilitiesLoaded
  }

  /**
   * Loads what the assistant offers this user (commands, suggestions, mention kinds) or why it
   * is not available to them. Once per page, unless `force`d: the answer can change while the
   * page is open (the host switches the feature off or on, the role's audience is closed).
   */
  loadCapabilities = async (force = false) => {
    await this.readCapabilities(force)
  }

  /** Returns whether the server answered (with the capabilities, or with a refusal). */
  private readCapabilities = async (force: boolean): Promise<boolean> => {
    if (this.capabilitiesLoaded && !force) {
      return true
    }
    const request = ++this.capabilitiesRequest
    const isNewest = () => request === this.capabilitiesRequest
    // A check made again behind the user's back says nothing when it fails: what was known
    // stays, and whoever asked for it (see checkAvailability) says so in its own place.
    const silent = this.capabilitiesLoaded
    try {
      const res = silent
        ? await ApiService.Assistant.GetCapabilities({ silent: true })
        : await ApiService.Assistant.GetCapabilities()
      runInAction(() => {
        if (!isNewest()) {
          return
        }
        this.capabilitiesLoadedAt = Date.now()
        const reason = res.data?.unavailableReason
        this.unavailableReason =
          typeof reason === 'string' && reason ? reason : null
        this.commands = res.data?.commands ?? []
        this.suggestions = res.data?.suggestedQueries ?? []
        this.mentionTypes = res.data?.mentionTypes ?? []
        this.voiceInput = res.data?.voiceInput === true
        const maxSeconds = Number(res.data?.voiceMaxSeconds)
        this.voiceMaxSeconds =
          Number.isFinite(maxSeconds) && maxSeconds > 0
            ? maxSeconds
            : DEFAULT_VOICE_MAX_SECONDS
        const notice = res.data?.privacyNotice
        this.privacyNotice =
          notice && typeof notice.version === 'string' && notice.version
            ? {
                version: notice.version,
                title: notice.title ?? '',
                lines: Array.isArray(notice.lines) ? notice.lines : [],
              }
            : null
        this.capabilitiesLoaded = true
      })
    } catch (error: any) {
      // 403: the user's role does not hold the assistant permission (or the tenant switch is
      // off). That is a state, not a failure — remember it and stop asking.
      if (error?.response?.status === 403) {
        const code = error.response.data?.error?.code
        runInAction(() => {
          if (!isNewest()) {
            return
          }
          this.unavailableReason =
            typeof code === 'string' && ASSISTANT_CODE_PATTERN.test(code)
              ? code
              : 'PERMISSION_DENIED'
          this.commands = []
          this.suggestions = []
          this.mentionTypes = []
          this.voiceInput = false
          this.privacyNotice = null
          this.capabilitiesLoaded = true
          this.capabilitiesLoadedAt = Date.now()
        })
        return true
      }
      // anything else: suggestions/commands are progressive enhancement — chat works without
      // them, and what was known before the request stays as it was
      return false
    }
    return true
  }

  /**
   * Asks the server again whether the assistant is available: after a turn was refused because
   * it is not (switched off after this page was loaded), and from the notice that says so.
   */
  reloadCapabilities = () => this.loadCapabilities(true)

  /**
   * "Check again" on the notice: the same question to the server, and whether it could be asked
   * at all — the notice says so when the answer changes nothing on screen.
   */
  checkAvailability = () => this.readCapabilities(true)

  /**
   * The user closed the privacy notice. The server keeps that with the user, so the notice is
   * not shown again on another device; only then does it go here. Returns whether it was saved.
   */
  acknowledgePrivacyNotice = async (): Promise<boolean> => {
    const notice = this.privacyNotice
    if (!notice) {
      return true
    }
    try {
      await ApiService.Assistant.AcknowledgePrivacyNotice(notice.version)
    } catch {
      return false
    }
    runInAction(() => {
      if (this.privacyNotice?.version === notice.version) {
        this.privacyNotice = null
      }
      // The question that was turned away for the notice is back in the composer; its warning
      // has been answered by closing the notice.
      for (const session of this.allSessions) {
        if (session.errorCode === PRIVACY_NOTICE_REQUIRED) {
          session.errorCode = null
        }
      }
    })
    return true
  }

  // ---- Conversation list --------------------------------------------------------------------

  /**
   * The history's filter changed and its answer has not arrived: the rows of the previous filter
   * stay on screen meanwhile, and nothing says "no match" before the server has.
   */
  get conversationsFiltering(): boolean {
    return this.conversationFilter.trim() !== this.loadedConversationFilter
  }

  /**
   * Reads the first page of the history (again). Older pages that were already loaded with
   * "Load more" stay: the first page is what changes when a turn ends (a new conversation, a
   * new title, another order).
   */
  loadConversations = async () => {
    this.conversationListRequested = true
    // A title that arrives while this request is under way is newer than its response.
    const seq = this.titleSeq
    const request = ++this.conversationListRequest
    const filter = this.conversationFilter.trim()
    try {
      const res = filter
        ? await ApiService.Assistant.GetConversations({ filter })
        : await ApiService.Assistant.GetConversations()
      runInAction(() => {
        if (request !== this.conversationListRequest) {
          return
        }
        const page = this.withStreamedTitles(res.data?.items ?? [], seq)
        const hasMore = res.data?.hasMore === true
        const oldest = page[page.length - 1]
        // What was loaded beyond the first page is older than all of it and is kept - unless
        // the rows on screen were read with another filter: those are not pages of this list.
        const sameList = filter === this.loadedConversationFilter
        this.loadedConversationFilter = filter
        const kept =
          sameList && hasMore && oldest
            ? this.conversations.filter(
                c =>
                  !page.some(item => item.id === c.id) &&
                  isOlder(c.lastMessageAt, oldest.lastMessageAt)
              )
            : []
        this.conversations = [...page, ...kept]
        this.conversationsHasMore = kept.length
          ? this.conversationsHasMore
          : hasMore
        this.conversationsTotal = Math.max(
          typeof res.data?.totalCount === 'number' ? res.data.totalCount : 0,
          this.conversations.length
        )
      })
    } catch {
      // list is non-critical; chat still works
      runInAction(() => {
        if (
          request === this.conversationListRequest &&
          filter !== this.loadedConversationFilter
        ) {
          // The rows on screen belong to another filter and this one's could not be read: they
          // must not stand for it.
          this.loadedConversationFilter = filter
          this.conversations = []
          this.conversationsHasMore = false
          this.conversationsTotal = 0
        }
      })
    }
  }

  /**
   * A turn of `session` ended (a new conversation, a new title, another order): the history is
   * read again. Always after the drawer's turns, as before; after a placement's only once the
   * list has been read on this page - a pane has no list on screen, and the drawer reads it when
   * it is opened.
   */
  refreshConversationsAfterTurn = (session: AssistantSession) => {
    if (session === this.drawer || this.conversationListRequested) {
      this.loadConversations()
    }
  }

  /** The next page of the history: the conversations older than the ones loaded so far. */
  loadMoreConversations = async () => {
    const last = this.conversations[this.conversations.length - 1]
    if (
      !last ||
      !this.conversationsHasMore ||
      this.loadingMoreConversations ||
      // The rows on screen are another filter's: their next page is not this filter's.
      this.conversationsFiltering
    ) {
      return
    }
    const seq = this.titleSeq
    const request = ++this.conversationListRequest
    const filter = this.conversationFilter.trim()
    this.loadingMoreConversations = true
    try {
      const res = await ApiService.Assistant.GetConversations({
        beforeLastMessageAt: last.lastMessageAt,
        ...(filter ? { filter } : {}),
      })
      runInAction(() => {
        if (request !== this.conversationListRequest) {
          return
        }
        const known = new Set(this.conversations.map(c => c.id))
        const page = this.withStreamedTitles(res.data?.items ?? [], seq)
        this.conversations = [
          ...this.conversations,
          ...page.filter(item => !known.has(item.id)),
        ]
        this.conversationsHasMore = res.data?.hasMore === true
        this.conversationsTotal = Math.max(
          typeof res.data?.totalCount === 'number' ? res.data.totalCount : 0,
          this.conversations.length
        )
      })
    } catch {
      // "Load more" stays where it is and can be pressed again
    } finally {
      runInAction(() => {
        this.loadingMoreConversations = false
      })
    }
  }

  /**
   * Filters the history by title. The server filters (all of the user's conversations, not only
   * the loaded ones), so the list is read anew from its first page. Until that page arrives the
   * rows of the previous filter stay where they are (conversationsFiltering says so): emptying
   * the list first would flash "nothing found" at a user who is still typing.
   */
  filterConversations = async (text: string) => {
    if (text.trim() === this.conversationFilter.trim()) {
      this.conversationFilter = text
      return
    }
    this.conversationFilter = text
    await this.loadConversations()
  }

  /** A title learned from the stream after the request was sent is newer than its response. */
  private withStreamedTitles = (
    items: AssistantConversationSummary[],
    seq: number
  ): AssistantConversationSummary[] =>
    items.map(item => {
      const streamed = this.streamedTitles.get(item.id)
      return streamed && streamed.seq > seq
        ? { ...item, title: streamed.title }
        : item
    })

  /**
   * The server has titled a conversation (after the first answer, or a rename): shown in the
   * history at once, without loading the list again. Called by the sessions' streams too.
   */
  applyTitle = (conversationId: string, title: string) => {
    if (!conversationId || !title) {
      return
    }
    this.streamedTitles.set(conversationId, { title, seq: ++this.titleSeq })
    const summary = this.conversations.find(c => c.id === conversationId)
    if (summary) {
      summary.title = title
    }
  }

  /**
   * Gives a conversation the name the user typed. The history shows what the server stored (the
   * name on one line, as it was written), without loading the list again; a list that was asked
   * for before the rename and answers after it does not bring the old title back.
   */
  renameConversation = async (id: string, title: string) => {
    const res = await ApiService.Assistant.RenameConversation(id, title)
    const stored = typeof res?.data?.title === 'string' ? res.data.title : ''
    runInAction(() => this.applyTitle(id, stored || title.trim()))
  }

  deleteConversation = async (id: string) => {
    await ApiService.Assistant.DeleteConversation(id)
    runInAction(() => {
      if (this.conversations.some(c => c.id === id)) {
        this.conversationsTotal = Math.max(0, this.conversationsTotal - 1)
      }
      this.conversations = this.conversations.filter(c => c.id !== id)
      for (const session of this.allSessions) {
        session.conversationDeleted(id)
      }
    })
  }

  // ---- Guide pages --------------------------------------------------------------------------

  /** Reads the list of guide pages, once per page load (see AssistantSession.loadDocs). */
  fetchDocs = async () => {
    if (this.docsLoaded) {
      return
    }
    try {
      const res = await ApiService.Assistant.GetDocs()
      runInAction(() => {
        this.docs = res.data ?? []
        this.docsLoaded = true
      })
    } catch {
      // the guide is optional — chat works without it
    }
  }

  loadDoc = async (key: string) => {
    if (this.docPages[key]) {
      return
    }
    try {
      const res = await ApiService.Assistant.GetDoc(key)
      runInAction(() => {
        if (res.data) {
          this.docPages[key] = res.data
        }
      })
    } catch {
      // keep the spinner-free empty page; the navigator still works
    }
  }

  // ---- Briefing -----------------------------------------------------------------------------

  /** Today's briefing (null until the user asked for it, or when unavailable). */
  briefing: AssistantBriefing | null = null
  briefingLoading = false
  /** The last load brought nothing: said next to the button that asked for it. */
  briefingFailed = false
  /** The briefing is shown in full; otherwise as one line (or, before it is loaded, a button). */
  briefingExpanded = false

  /**
   * Shows the briefing in full, loading it the first time. It is only loaded when asked for: its
   * overdue counts are expensive on the server, and not every visit to the assistant reads them.
   */
  showBriefing = async () => {
    this.briefingExpanded = true
    if (!this.briefing) {
      await this.loadBriefing()
    }
  }

  collapseBriefing = () => {
    this.briefingExpanded = false
  }

  /** Loads the briefing; the server caches it for a while unless `refresh`ed. */
  loadBriefing = async (refresh = false) => {
    if (this.briefingLoading) {
      return
    }
    this.briefingLoading = true
    this.briefingFailed = false
    try {
      const res = await ApiService.Assistant.GetBriefing(refresh)
      runInAction(() => {
        if (res.data) {
          this.briefing = res.data
        } else {
          this.briefingFailed = true
        }
      })
    } catch {
      // the briefing is a convenience — chat works without it
      runInAction(() => {
        this.briefingFailed = true
      })
    } finally {
      runInAction(() => {
        this.briefingLoading = false
      })
    }
  }

  // ---- The drawer's session, read on the store ----------------------------------------------
  // Callers outside a panel (the Quick Action menu, the standalone page) and the existing tests
  // use these; a component inside a panel reads its own session (useAssistantSession).

  get showHistory() {
    return this.drawer.showHistory
  }
  set showHistory(value: boolean) {
    this.drawer.showHistory = value
  }

  get showGuide() {
    return this.drawer.showGuide
  }
  set showGuide(value: boolean) {
    this.drawer.showGuide = value
  }

  get guideKey() {
    return this.drawer.guideKey
  }
  set guideKey(value: string | null) {
    this.drawer.guideKey = value
  }

  get guideSection() {
    return this.drawer.guideSection
  }
  set guideSection(value: string | null) {
    this.drawer.guideSection = value
  }

  get activeConversationId() {
    return this.drawer.activeConversationId
  }
  set activeConversationId(value: string | null) {
    this.drawer.activeConversationId = value
  }

  get messages() {
    return this.drawer.messages
  }
  set messages(value: AssistantChatMessage[]) {
    this.drawer.messages = value
  }

  get hasEarlierMessages() {
    return this.drawer.hasEarlierMessages
  }
  set hasEarlierMessages(value: boolean) {
    this.drawer.hasEarlierMessages = value
  }

  get loadingEarlierMessages() {
    return this.drawer.loadingEarlierMessages
  }
  set loadingEarlierMessages(value: boolean) {
    this.drawer.loadingEarlierMessages = value
  }

  get status() {
    return this.drawer.status
  }
  set status(value: AssistantSession['status']) {
    this.drawer.status = value
  }

  get loadingConversationId() {
    return this.drawer.loadingConversationId
  }
  set loadingConversationId(value: string | null) {
    this.drawer.loadingConversationId = value
  }

  get turn() {
    return this.drawer.turn
  }
  set turn(value: AssistantSession['turn']) {
    this.drawer.turn = value
  }

  get errorCode() {
    return this.drawer.errorCode
  }
  set errorCode(value: string | null) {
    this.drawer.errorCode = value
  }

  get draftMentions() {
    return this.drawer.draftMentions
  }
  set draftMentions(value: AssistantDraftMention[]) {
    this.drawer.draftMentions = value
  }

  get draftText() {
    return this.drawer.draftText
  }
  set draftText(value: string) {
    this.drawer.draftText = value
  }

  get draftRevision() {
    return this.drawer.draftRevision
  }
  set draftRevision(value: number) {
    this.drawer.draftRevision = value
  }

  get composerFocusRequest() {
    return this.drawer.composerFocusRequest
  }
  set composerFocusRequest(value: number) {
    this.drawer.composerFocusRequest = value
  }

  get isStandalone() {
    return this.drawer.isStandalone
  }
  set isStandalone(value: boolean) {
    this.drawer.isStandalone = value
  }

  get standaloneContext() {
    return this.drawer.standaloneContext
  }
  set standaloneContext(value: AssistantPageContext | null) {
    this.drawer.standaloneContext = value
  }

  get actionInFlight() {
    return this.drawer.actionInFlight
  }
  set actionInFlight(value: string | null) {
    this.drawer.actionInFlight = value
  }

  get streamingContent() {
    return this.drawer.streamingContent
  }

  get toolActivity() {
    return this.drawer.toolActivity
  }

  get isTurnRunning() {
    return this.drawer.isTurnRunning
  }

  get loadingConversation() {
    return this.drawer.loadingConversation
  }

  get isBusy() {
    return this.drawer.isBusy
  }

  get isInputLocked() {
    return this.drawer.isInputLocked
  }

  get errorMessage() {
    return this.drawer.errorMessage
  }

  get canRetry() {
    return this.drawer.canRetry
  }

  get pendingClarification() {
    return this.drawer.pendingClarification
  }

  get activePageContext() {
    return this.drawer.activePageContext
  }

  get pageContextRefused() {
    return this.drawer.pageContextRefused
  }

  get pageContextRefusedIsList() {
    return this.drawer.pageContextRefusedIsList
  }

  dismissPageContext: AssistantSession['dismissPageContext'] = () =>
    this.drawer.dismissPageContext()
  enterStandalone: AssistantSession['enterStandalone'] = (...args) =>
    this.drawer.enterStandalone(...args)
  requestComposerFocus: AssistantSession['requestComposerFocus'] = () =>
    this.drawer.requestComposerFocus()
  applySuggestion: AssistantSession['applySuggestion'] = (...args) =>
    this.drawer.applySuggestion(...args)
  editDraft: AssistantSession['editDraft'] = (...args) =>
    this.drawer.editDraft(...args)
  setDraft: AssistantSession['setDraft'] = (...args) =>
    this.drawer.setDraft(...args)
  dismissError: AssistantSession['dismissError'] = () =>
    this.drawer.dismissError()
  registerDraftMention: AssistantSession['registerDraftMention'] = (...args) =>
    this.drawer.registerDraftMention(...args)
  newConversation: AssistantSession['newConversation'] = () =>
    this.drawer.newConversation()
  openInNewWindow: AssistantSession['openInNewWindow'] = () =>
    this.drawer.openInNewWindow()
  toggleHistory: AssistantSession['toggleHistory'] = () =>
    this.drawer.toggleHistory()
  openGuide: AssistantSession['openGuide'] = (...args) =>
    this.drawer.openGuide(...args)
  toggleGuide: AssistantSession['toggleGuide'] = () => this.drawer.toggleGuide()
  loadDocs: AssistantSession['loadDocs'] = () => this.drawer.loadDocs()
  tryPrompt: AssistantSession['tryPrompt'] = (...args) =>
    this.drawer.tryPrompt(...args)
  selectConversation: AssistantSession['selectConversation'] = (...args) =>
    this.drawer.selectConversation(...args)
  loadEarlierMessages: AssistantSession['loadEarlierMessages'] = () =>
    this.drawer.loadEarlierMessages()
  sendMessage: AssistantSession['sendMessage'] = (...args) =>
    this.drawer.sendMessage(...args)
  answerClarification: AssistantSession['answerClarification'] = (...args) =>
    this.drawer.answerClarification(...args)
  retryLastTurn: AssistantSession['retryLastTurn'] = () =>
    this.drawer.retryLastTurn()
  decideAction: AssistantSession['decideAction'] = (...args) =>
    this.drawer.decideAction(...args)
  stop: AssistantSession['stop'] = () => this.drawer.stop()
  submitFeedback: AssistantSession['submitFeedback'] = (...args) =>
    this.drawer.submitFeedback(...args)
}

const assistantStore = new AssistantStore()

export default assistantStore
